import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import "./ReleaseNotesPopup.css";
import Alert from "@mui/material/Alert";
import { baseURL } from "./Constant";
import ReleaseNotesUploadFile from "./ReleaseNotesUploadFile";
import ExcelJS from 'exceljs';
import * as XLSX from "xlsx";
import { htmlToReact } from "html-to-react";
// import CloseIcon from '@mui/icons-material/Close';

const ReleaseNotesPopup = ({ isOpen, onClose }) => {
  const [isEditing, setEditing] = useState(false);
  const [content, setContent] = useState({});
  const [newSection, setNewSection] = useState("");
  const [newContent, setNewContent] = useState("");
  const [openAlert, setOpenAlert] = useState(false);
  const [editingOnText, setEditingOnText] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [sectionTitleMap, setSectionTitleMap] = useState(new Map());
  const [error, setError] = useState(false); 

  const htmlToExcelData = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
  
    const data = [];
  
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return { text: node.textContent.replace(/\n/g, "\n"), style: {} };
      }
  
      if (node.nodeName === "UL") {
        const bulletItems = Array.from(node.children).map(li => {
          return `• ${li.textContent.trim()}`;
        });
        return { text: bulletItems.join("\n"), style: {} };
      }
  
      const children = Array.from(node.childNodes).map(processNode);
      const style = {};
  
      if (node.style.fontWeight === "bold") {
        style.fontWeight = "bold";
      }
      if (node.style.fontStyle === "italic") {
        style.fontStyle = "italic";
      }
  
      return { text: node.innerText.replace(/\n/g, "\n"), style, children };
    };
  
    const formattedData = Array.from(tempDiv.childNodes).map(processNode);

    formattedData.forEach(({ text, style }) => {
      data.push({ text, style });
    });
  
    return data;
  };
  
  

  const handleSectionTitleChange = (oldTitle, newTitle) => {
    setSectionTitleMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(oldTitle, newTitle);
      return newMap;
    });
  };

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Release Notes");
  
    worksheet.addRow(["Section", "Content"]);
    
    const maxLengths = [0, 0];
  
    Object.entries(content).forEach(([section, value]) => {
      const formattedData = htmlToExcelData(value);
      
      formattedData.forEach(({ text }) => {
        const row = worksheet.addRow([section, text]);

        maxLengths[0] = Math.max(maxLengths[0], section.length);
        maxLengths[1] = Math.max(maxLengths[1], text.length);
        
        row.getCell(2).alignment = { wrapText: true };
      });
    });
  

    worksheet.getColumn(1).width = maxLengths[0] + 2; 
    worksheet.getColumn(2).width = maxLengths[1] + 2; 
  
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "release_notes.xlsx";
      link.click();
    });
  };
  

  useEffect(() => {
    // if(isEditing){
    //     setEditingOnText(true)
    // }
    // setTimeout(() => {
    //     setEditingOnText(false)
    // }, 3000)

    if (isEditing) {
      setEditingOnText(true);
    } else {
      setEditingOnText("show");
      // Show "Editing mode is off" for 3 seconds
      const timer = setTimeout(() => {
        setEditingOnText(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isEditing]);

  useEffect(() => {
    if (isOpen) {
      const fetchReleaseNotes = async () => {
        try {
          const response = await axios.get(`${baseURL}/releaseNotes`);
          const notes = {};
          response.data.forEach((note) => {
            notes[note.section] = note.content;
          });
          setContent(notes);
        } catch (error) {
          console.error("Error fetching release notes:", error);
        }
      };

      fetchReleaseNotes();
    }
  }, [isOpen, uploadDialogOpen]);

  const handleEditClick = () => {
    setEditing(!isEditing);
  };

  const handleContentChange = (section, value) => {
    setContent((prevContent) => ({ ...prevContent, [section]: value }));
  };

  // const handleSave = async (section) => {
  //   try {
  //     await axios.post(`${baseURL}/releaseNotes`, {
  //       section,
  //       content: content[section],
  //     });
  //     //   alert('Content saved successfully!');
  //     setOpenAlert("success");
  //   } catch (error) {
  //     console.error("Error saving content:", error);
  //     //   alert('Failed to save content.');
  //     setOpenAlert("failed");
  //   }
  // };

  const handleSave = async (section) => {
    const newSectionTitle = sectionTitleMap.get(section) || section;
    try {
      await axios.put(`${baseURL}/releaseNotesUpdate`, {
        oldSection: section,
        newSection: newSectionTitle,
        content: content[section],
      });
      setContent((prevContent) => {
        const { [section]: oldSectionContent, ...remaining } = prevContent;
        return { ...remaining, [newSectionTitle]: oldSectionContent };
      });
      setOpenAlert("success");
    } catch (error) {
      console.error("Error saving content:", error);
      setOpenAlert("failed");
    }
  };

  const handleAddSection = async () => {
    if (!newSection.trim()) {
      setError(true);
      return;
    }

    setError(false);

    try {
      await axios.post(`${baseURL}/releaseNotes`, {
        section: newSection,
        content: newContent,
      });
      setContent((prevContent) => ({
        ...prevContent,
        [newSection]: newContent,
      }));
      setNewSection("");
      setNewContent("");
      setOpenAlert("success");
    } catch (error) {
      console.error("Error adding new section:", error);
      setOpenAlert("failed");
    }
  };

  const resetNewSectionFields = () => {
    setNewSection("");
    setNewContent("");
    setEditing(false);
    setError(false); 
  };

  const handleEditToggle = () => {
    setEditing(!isEditing);
  };

  const handleCancelEditing = () => {
    setEditing(false);
  };

  const formatContent = (text) => {
    return text.replace(/\n/g, "<br />"); // Convert newlines to <br />
  };

  useEffect(() => {
    setTimeout(() => {
      setOpenAlert(false);
    }, 3000);
  }, [openAlert]);

  const handleDeleteSection = async (section) => {
    try {
      await axios.delete(`${baseURL}/releaseNotes`, { data: { section } });
      setContent((prevContent) => {
        const { [section]: removed, ...remainingContent } = prevContent;
        return remainingContent;
      });
      setOpenAlert("success");
    } catch (error) {
      console.error("Error deleting section:", error);
      setOpenAlert("failed");
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Release Notes
        {openAlert == "success" ? (
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setOpenAlert(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity="success"
            sx={{ mb: 2 }}
          >
            Updated release notes successfully!
          </Alert>
        ) : (
          openAlert == "failed" && (
            <Alert
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setOpenAlert(false);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              severity="error"
              sx={{ mb: 2 }}
            >
              Failed to update data!
            </Alert>
          )
        )}
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{ position: "absolute", right: 40, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
        {/* <IconButton edge="end" color="inherit" onClick={handleEditClick} aria-label="edit" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <EditIcon />
        </IconButton> */}
      </DialogTitle>
      <DialogContent dividers>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setEditing(true)} // Allow adding new sections while in edit mode
          //   style={{ marginBottom: '10px' }}
        >
          Add New Section
        </Button>
        <Button
          variant="outlined"
          onClick={() => setUploadDialogOpen(true)} // Open the upload dialog
          style={{ marginLeft: "10px" }}
        >
          Upload Excel
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownloadExcel}
          style={{ marginLeft: "10px" }}
        >
          Download Excel
        </Button>
        {/* <IconButton
          edge="end"
          color="inherit"
          onClick={handleEditToggle}
          aria-label="edit"
          sx={{ marginLeft: 1 }}
        >
          <EditIcon />

        </IconButton> */}
        <Button
          variant="contained"
          onClick={handleEditToggle}
          style={{
            marginLeft: "8px",
            // marginTop: '-10px',
            backgroundColor: isEditing ? "green" : "grey",
            color: "white",
          }}
        >
          {/* {isEditing ? 'Editing Mode On' : 'Edit Section'} */}
          Edit Section
        </Button>
        {/* {isEditing ?  <Typography variant="body2" sx={{ display: 'inline', marginLeft: 1 }}>
            {editingOnText && <>Editing mode is on</>}</Typography>  : <Typography variant="body2" sx={{ display: 'inline', marginLeft: 1 }}>Editing mode is off</Typography>} */}

        {isEditing ? (
          <Typography variant="body2" sx={{ display: "inline", marginLeft: 1 }}>
            Editing mode is on
          </Typography>
        ) : (
          editingOnText == "show" && (
            <Typography
              variant="body2"
              sx={{ display: "inline", marginLeft: 1 }}
            >
              Editing mode is off
            </Typography>
          )
        )}
        {Object.entries(content).map(([section]) => (
          <Accordion key={section}>
            <AccordionSummary className="spl" sx={{padding:"0px 16px", margin:"0px"}} expandIcon={<ExpandMoreIcon />}>
              {isEditing ? (
                <TextField
                  value={sectionTitleMap.get(section)===undefined || sectionTitleMap.get(section)===null? 
                    section: sectionTitleMap.get(section)}
                  onChange={(e) =>
                    handleSectionTitleChange(section, e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  sx={{ marginRight: 1 }}
                />
              ) : (
                <Typography sx={{margin:"0px"}} variant="h6">
                  {section.replace(/([A-Z])/g, " $1").trim()}
                </Typography>
              )}
              {isEditing && (
                <IconButton
                  //   onClick={() => handleDeleteSection(section)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSection(section);
                  }}
                  aria-label="delete"
                  sx={{ marginLeft: "auto"}}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </AccordionSummary>
            <AccordionDetails>
              {isEditing ? (
                <div>
                  <ReactQuill
                    value={content[section]}
                    onChange={(value) => handleContentChange(section, value)}
                    placeholder="Start typing..."
                  />
                  <Button
                    onClick={() => handleSave(section)}
                    variant="contained"
                    color="primary"
                    style={{ marginTop: "10px" }}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={handleCancelEditing}
                    variant="outlined"
                    color="secondary"
                    style={{ marginLeft: "10px", marginTop: "10px" }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Typography
                  component="div"
                  variant="body2"
                  className="release-notes-wrap"
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: formatContent(content[section]),
                    }}
                  />
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
        {isEditing && (
          <div style={{ marginTop: "20px" }}>
            <TextField
              label="Section Title"
              value={newSection}
              onChange={(e) => setNewSection(e.target.value)}
              fullWidth
              margin="normal"
              error={error} 
              helperText={error ? "Section title cannot be empty" : ""}
            />
            <ReactQuill
              value={newContent}
              onChange={setNewContent}
              placeholder="Content for new section..."
              style={{ marginBottom: "10px" }}
            />
            <Button
              onClick={handleAddSection}
              variant="contained"
              color="primary"
            >
              Add Section
            </Button>
            <Button
              onClick={resetNewSectionFields}
              variant="outlined"
              color="secondary"
              style={{ marginLeft: "10px" }}
            >
              Cancel
            </Button>
          </div>
        )}
      </DialogContent>
      <ReleaseNotesUploadFile
        isOpen={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
      />
    </Dialog>
  );
};

export default ReleaseNotesPopup;
